import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MdCheckCircle,
  MdChevronRight,
  MdEdit,
  MdExpandMore,
  MdMail,
  MdNotes,
  MdSend,
  MdWarning,
} from "react-icons/md";
import { getApiErrorMessage } from "api";
import { profilePath } from "components/assessment/audiences";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import Card, { CardHeader } from "components/card";
import Textarea from "components/fields/Textarea";
import { EmptyState } from "components/states";
import { SegmentedControl } from "components/tabs";
import notify from "lib/notify";
import {
  getRatingLabel,
  getRatingTextClass,
  getRatingTone,
  getResponseHealth,
  hasBlocker,
} from "utils/healthScoring";
import { formatDate } from "utils/format";

const FOLLOW_UP_METHODS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

/** Group responses by person, merge roles, and pick the most recent as primary. */
function groupByPerson(responses) {
  const map = {};
  (responses || []).forEach((r) => {
    const key = r.staffName || "Unknown";
    if (!map[key]) {
      map[key] = { name: key, userId: r.userId, roles: new Set(), responses: [] };
    }
    if (r.staffRole) map[key].roles.add(r.staffRole);
    map[key].responses.push(r);
  });

  return Object.values(map).map((group) => {
    group.responses.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
    return {
      name: group.name,
      userId: group.userId,
      roles: [...group.roles].join(" · "),
      primary: group.responses[0],
      extras: group.responses.slice(1),
    };
  });
}

/** Same person's responses from the weeks before `selectedWeek`, newest first. */
function getPriorResponses(personName, allSummaries, selectedWeek) {
  if (!allSummaries || !selectedWeek) return [];

  const selectedIdx = allSummaries.findIndex((s) => s.weekOf === selectedWeek);
  if (selectedIdx < 0) return [];

  const prior = [];
  for (let i = selectedIdx + 1; i < allSummaries.length; i++) {
    const weekResponses = allSummaries[i]?.responses || [];
    const match = weekResponses.find((r) => r.staffName === personName);
    if (match) prior.push(match);
  }
  return prior;
}

function ScoreBadge({ label, value }) {
  if (!value || value <= 0) return null;
  return (
    <Badge tone={getRatingTone(value)}>
      {label} {value}/5
    </Badge>
  );
}

function ActionButtons({ response, service, onUpdate }) {
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [noteText, setNoteText] = useState(response.followUpNotes || "");
  const [followUpMessage, setFollowUpMessage] = useState("");
  const [followUpError, setFollowUpError] = useState("");
  const [followUpMethod, setFollowUpMethod] = useState("email");
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [localAddressedAt, setLocalAddressedAt] = useState(response.addressedAt);
  const [localNotes, setLocalNotes] = useState(response.followUpNotes || "");

  const isReviewed = !!localAddressedAt;

  const handleMarkReviewed = async () => {
    if (isReviewed) return;
    setMarkingReviewed(true);
    try {
      await service.addressCareTask(response.id, {});
      setLocalAddressedAt(new Date().toISOString());
      notify.success("Marked as reviewed");
      onUpdate?.();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not mark as reviewed"));
    } finally {
      setMarkingReviewed(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await service.addressCareTask(response.id, { followUpNotes: noteText });
      setLocalNotes(noteText);
      setShowNoteForm(false);
      notify.success("Note saved");
      onUpdate?.();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save note"));
    } finally {
      setSavingNote(false);
    }
  };

  const handleSendFollowUp = async () => {
    if (!followUpMessage.trim()) {
      setFollowUpError("Enter a message");
      return;
    }
    setSendingFollowUp(true);
    try {
      const res = await service.sendFollowUp(response.id, {
        message: followUpMessage,
        method: followUpMethod,
      });
      notify.success(res.data?.message || "Follow-up sent");
      setFollowUpMessage("");
      setShowFollowUpForm(false);
      setLocalAddressedAt(res.data?.addressedAt || new Date().toISOString());
      if (res.data?.followUpNotes) setLocalNotes(res.data.followUpNotes);
      onUpdate?.();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send follow-up"));
    } finally {
      setSendingFollowUp(false);
    }
  };

  const toggleFollowUpForm = () => {
    setShowFollowUpForm((open) => !open);
    setShowNoteForm(false);
  };

  const toggleNoteForm = () => {
    setShowNoteForm((open) => !open);
    setShowFollowUpForm(false);
    setNoteText(localNotes);
  };

  return (
    <div className="space-y-2">
      {localNotes && !showNoteForm && (
        <div className="rounded-control bg-info-subtle p-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-info-fg">
            <MdNotes aria-hidden="true" className="h-3.5 w-3.5" />
            Private Note
          </p>
          <p className="whitespace-pre-wrap text-sm text-fg">{localNotes}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<MdMail />}
          aria-expanded={showFollowUpForm}
          onClick={toggleFollowUpForm}
        >
          Send Follow-Up
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<MdEdit />}
          aria-expanded={showNoteForm}
          onClick={toggleNoteForm}
        >
          {localNotes ? "Edit Note" : "Add Note"}
        </Button>
        {isReviewed ? (
          <Badge tone="success">
            <MdCheckCircle aria-hidden="true" className="h-3.5 w-3.5" />
            Reviewed
          </Badge>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<MdCheckCircle />}
            loading={markingReviewed}
            onClick={handleMarkReviewed}
          >
            Mark Reviewed
          </Button>
        )}
      </div>

      {showNoteForm && (
        <div className="rounded-control border border-line bg-surface-subtle p-3">
          <Textarea
            label="Private Note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a private note about this response…"
            rows={3}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowNoteForm(false)}>
              Cancel
            </Button>
            <Button size="sm" loading={savingNote} onClick={handleSaveNote}>
              Save Note
            </Button>
          </div>
        </div>
      )}

      {showFollowUpForm && (
        <div className="rounded-control border border-line bg-surface-subtle p-3">
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-medium text-fg-muted">Send via</span>
            <SegmentedControl
              aria-label="Send via"
              options={FOLLOW_UP_METHODS}
              value={followUpMethod}
              onChange={setFollowUpMethod}
            />
          </div>
          <Textarea
            label="Message"
            value={followUpMessage}
            onChange={(e) => {
              setFollowUpMessage(e.target.value);
              if (followUpError) setFollowUpError("");
            }}
            error={followUpError}
            placeholder={`Write your ${followUpMethod === "email" ? "email" : "text"} message to ${response.staffName}…`}
            rows={3}
            required
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowFollowUpForm(false)}>
              Cancel
            </Button>
            <Button size="sm" leftIcon={<MdSend />} loading={sendingFollowUp} onClick={handleSendFollowUp}>
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <h3 className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">{title}</h3>
      {children}
    </div>
  );
}

function RatingText({ label, rating }) {
  return (
    <span>
      {label}:{" "}
      <span className={`font-semibold ${getRatingTextClass(rating)}`}>
        {rating}/5 · {getRatingLabel(rating)}
      </span>
    </span>
  );
}

function ExpandedDetails({ response, service, onUpdate, questionLabels }) {
  return (
    <div className="mt-3 space-y-3 border-t border-line pt-3">
      {hasBlocker(response) && (
        <div className="flex items-start gap-2 rounded-control bg-warning-subtle p-3">
          <MdWarning aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning-fg" />
          <div>
            <h3 className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-warning-fg">
              {questionLabels.blockers || "Blocker"}
            </h3>
            <p className="text-sm text-fg">{response.blockers}</p>
          </div>
        </div>
      )}

      {response.mainWork && (
        <DetailSection title={questionLabels.mainWork || "Focus This Week"}>
          <p className="text-sm text-fg">{response.mainWork}</p>
        </DetailSection>
      )}

      {response.story && (
        <DetailSection title={questionLabels.story || "Story / Highlight"}>
          <p className="text-sm text-fg">{response.story}</p>
        </DetailSection>
      )}

      {response.aiSummary && (
        <DetailSection title="AI Insight">
          <p className="text-sm italic text-fg-muted">{response.aiSummary}</p>
        </DetailSection>
      )}

      <div className="flex flex-wrap items-center gap-4 text-xs text-fg-muted">
        {response.roleSatisfaction > 0 && (
          <RatingText
            label={questionLabels.roleSatisfaction || "Satisfaction"}
            rating={response.roleSatisfaction}
          />
        )}
        {response.contributionScore > 0 && (
          <RatingText
            label={questionLabels.contributionScore || "Contribution"}
            rating={response.contributionScore}
          />
        )}
        {response.submittedAt && <span>Submitted: {formatDate(response.submittedAt)}</span>}
      </div>

      <ActionButtons response={response} service={service} onUpdate={onUpdate} />
    </div>
  );
}

function ExtraSubmissions({ extras, service, onUpdate, questionLabels }) {
  const [open, setOpen] = useState(false);
  if (!extras || extras.length === 0) return null;

  return (
    <div className="mt-3 border-t border-line pt-3">
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        leftIcon={
          <MdExpandMore
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        }
        onClick={() => setOpen((o) => !o)}
      >
        {extras.length} additional submission{extras.length > 1 ? "s" : ""}
      </Button>
      {open && (
        <div className="mt-2 space-y-3">
          {extras.map((r) => (
            <div key={r.id} className="rounded-control border border-line bg-surface-subtle p-3">
              <p className="mb-1 text-xs text-fg-muted">
                Role: <span className="capitalize">{r.staffRole}</span>
                {r.submittedAt && <> · {formatDate(r.submittedAt)}</>}
              </p>
              <ExpandedDetails
                response={r}
                service={service}
                onUpdate={onUpdate}
                questionLabels={questionLabels}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonCard({ config, group, allSummaries, selectedWeek, onUpdate, questionLabels }) {
  const [expanded, setExpanded] = useState(false);
  const { name, userId, roles, primary, extras } = group;
  const toggle = () => setExpanded((e) => !e);

  const priorResponses = useMemo(
    () => getPriorResponses(name, allSummaries, selectedWeek),
    [name, allSummaries, selectedWeek]
  );

  const { badge } = getResponseHealth(primary, priorResponses);

  return (
    <li className="rounded-control border border-line bg-surface p-4">
      {/* The summary area toggles on click; the chevron button is the keyboard control. */}
      <div
        className="cursor-pointer"
        onClick={(e) => {
          if (e.target.closest("a, button")) return;
          toggle();
        }}
      >
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={name} size="sm" noBorder />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {userId ? (
                  <Link
                    to={profilePath(config, userId)}
                    className="focus-ring truncate rounded font-medium text-primary hover:underline dark:text-accent"
                  >
                    {name}
                  </Link>
                ) : (
                  <p className="truncate font-medium text-fg">{name}</p>
                )}
                {extras.length > 0 && (
                  <Badge tone="brand" size="sm">
                    {extras.length + 1} submissions
                  </Badge>
                )}
              </div>
              <p className="truncate text-xs capitalize text-fg-muted">{roles}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ScoreBadge label="Satisfaction" value={primary.roleSatisfaction} />
            <ScoreBadge label="Contribution" value={primary.contributionScore} />
            {primary.addressedAt && (
              <Badge tone="success">
                <MdCheckCircle aria-hidden="true" className="h-3 w-3" />
                Reviewed
              </Badge>
            )}
            <Badge tone={badge.tone}>{badge.label}</Badge>
            <IconButton
              size="sm"
              label={expanded ? `Hide details for ${name}` : `Show details for ${name}`}
              aria-expanded={expanded}
              icon={
                <MdChevronRight
                  aria-hidden="true"
                  className={`transition-transform ${expanded ? "rotate-90" : ""}`}
                />
              }
              onClick={toggle}
            />
          </div>
        </div>

        {!expanded && (
          <p className="mt-2 line-clamp-2 text-sm text-fg-muted" title={primary.mainWork || ""}>
            {primary.mainWork || <span className="italic">No focus submitted</span>}
          </p>
        )}
      </div>

      {expanded && (
        <>
          <ExpandedDetails
            response={primary}
            service={config.service}
            onUpdate={onUpdate}
            questionLabels={questionLabels}
          />
          <ExtraSubmissions
            extras={extras}
            service={config.service}
            onUpdate={onUpdate}
            questionLabels={questionLabels}
          />
        </>
      )}
    </li>
  );
}

const ResponsesTable = ({ config, responses, allSummaries, selectedWeek, onUpdate, pulseQuestions }) => {
  const grouped = useMemo(() => groupByPerson(responses), [responses]);

  // Question text by response field; displayOrder 1-5 maps to the fields below.
  const questionLabels = useMemo(() => {
    const labels = {};
    if (!pulseQuestions || pulseQuestions.length === 0) return labels;
    const fieldsByOrder = {
      1: "story",
      2: "mainWork",
      3: "blockers",
      4: "roleSatisfaction",
      5: "contributionScore",
    };
    [...pulseQuestions]
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .forEach((q) => {
        const field = fieldsByOrder[q.displayOrder];
        if (field) labels[field] = q.question;
      });
    return labels;
  }, [pulseQuestions]);

  return (
    <Card padding="md">
      <CardHeader
        title="Leadership Snapshot"
        actions={
          grouped.length > 0 && (
            <span className="text-xs text-fg-muted">
              {grouped.length} team member{grouped.length !== 1 ? "s" : ""}
            </span>
          )
        }
      />
      {grouped.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-3">
          {grouped.map((group) => (
            <PersonCard
              key={group.name}
              config={config}
              group={group}
              allSummaries={allSummaries}
              selectedWeek={selectedWeek}
              onUpdate={onUpdate}
              questionLabels={questionLabels}
            />
          ))}
        </ul>
      ) : (
        <EmptyState variant="inline" title="No responses yet this week" />
      )}
    </Card>
  );
};

export default ResponsesTable;
